import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Upload, X } from 'lucide-react';

import { greyP4, greyP5 } from '../../theme';
import { ProductVariant } from '../../types/catalogue';

interface VariantFormData {
  name: string;
  power: number;
  lumenOutput: number;
  colorTemperature: number;
  manufacturerUrl?: string;
}

interface AddVariantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddVariant: (data: VariantFormData, ldcFile?: File) => void;
  productName?: string;
  editData?: ProductVariant | null;
}

const AddVariantModal: React.FC<AddVariantModalProps> = ({
  isOpen,
  onClose,
  onAddVariant,
  productName: _productName,
  editData,
}) => {
  const isEditMode = !!editData;
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [power, setPower] = useState('');
  const [lumenOutput, setLumenOutput] = useState('');
  const [colorTemperature, setColorTemperature] = useState('');
  const [manufacturerUrl, setManufacturerUrl] = useState('');
  const [ldcFile, setLdcFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && editData) {
      setName(editData.name || '');
      setPower(editData.power != null ? String(editData.power) : '');
      setLumenOutput(editData.lumenOutput != null ? String(editData.lumenOutput) : '');
      setColorTemperature(editData.colorTemperature != null ? String(editData.colorTemperature) : '');
      setManufacturerUrl(editData.manufacturerUrl || '');
      setLdcFile(null);
    } else if (!isOpen) {
      setName('');
      setPower('');
      setLumenOutput('');
      setColorTemperature('');
      setManufacturerUrl('');
      setLdcFile(null);
    }
  }, [isOpen, editData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const ldcRequired = !isEditMode;
    if (name.trim() && power && lumenOutput && colorTemperature && (ldcFile || !ldcRequired)) {
      const data: VariantFormData = {
        name: name.trim(),
        power: parseFloat(power),
        lumenOutput: parseFloat(lumenOutput),
        colorTemperature: parseInt(colorTemperature, 10),
      };
      if (manufacturerUrl.trim()) data.manufacturerUrl = manufacturerUrl.trim();
      onAddVariant(data, ldcFile ?? undefined);
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLdcFile(file);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setLdcFile(file);
    }
  };

  const cctValue = colorTemperature ? parseInt(colorTemperature, 10) : undefined;
  const cctValid = !colorTemperature || (cctValue !== undefined && cctValue >= 1000);
  const urlValid = !manufacturerUrl.trim() || /^https?:\/\/.+/.test(manufacturerUrl.trim());

  const isValid =
    name.trim() &&
    power &&
    lumenOutput &&
    colorTemperature &&
    parseFloat(power) > 0 &&
    parseFloat(lumenOutput) > 0 &&
    parseInt(colorTemperature, 10) >= 1000 &&
    (ldcFile || isEditMode) &&
    cctValid &&
    urlValid;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-variant-modal-title"
        className="w-full max-w-md rounded-lg p-6"
        style={{ backgroundColor: greyP5 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 id="add-variant-modal-title" className="text-lg font-semibold text-white">
            {isEditMode ? t('catalogue.editVariantTitle') : t('catalogue.addVariantTitle')}
          </h2>
          <button
            onClick={onClose}
            className="text-greyP3 hover:text-white transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-greyP3 mb-2">
              {t('catalogue.variantName')} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('catalogue.variantNamePlaceholder')}
              className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                border focus:border-green01 focus:outline-none transition-colors"
              style={{ borderColor: greyP4 }}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-greyP3 mb-2">
                {t('catalogue.power')} (W) <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                value={power}
                onChange={(e) => setPower(e.target.value)}
                placeholder="e.g. 95"
                min="0"
                step="0.1"
                className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                  border focus:border-green01 focus:outline-none transition-colors"
                style={{ borderColor: greyP4 }}
              />
            </div>
            <div>
              <label className="block text-sm text-greyP3 mb-2">
                {t('catalogue.lumenOutput')} (lm) <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                value={lumenOutput}
                onChange={(e) => setLumenOutput(e.target.value)}
                placeholder="e.g. 12500"
                min="0"
                step="1"
                className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                  border focus:border-green01 focus:outline-none transition-colors"
                style={{ borderColor: greyP4 }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-greyP3 mb-2">
                {t('catalogue.colorTemperature')} (K) <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                value={colorTemperature}
                onChange={(e) => setColorTemperature(e.target.value)}
                placeholder="e.g. 3000"
                min="1000"
                step="1"
                className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                  border focus:border-green01 focus:outline-none transition-colors"
                style={{ borderColor: greyP4 }}
              />
              {colorTemperature && !cctValid && (
                <p className="text-red-400 text-xs mt-1">{t('catalogue.colorTemperatureMin')}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-greyP3 mb-2">{t('catalogue.manufacturerUrl')}</label>
              <input
                type="url"
                value={manufacturerUrl}
                onChange={(e) => setManufacturerUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                  border focus:border-green01 focus:outline-none transition-colors"
                style={{ borderColor: greyP4 }}
              />
              {manufacturerUrl.trim() && !urlValid && (
                <p className="text-red-400 text-xs mt-1">{t('catalogue.manufacturerUrlInvalid')}</p>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm text-greyP3 mb-2">
              {t('catalogue.ldcFile')} {!isEditMode && <span className="text-red-400">*</span>}
            </label>
            <div
              className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
                hover:border-green01 transition-colors"
              style={{ borderColor: greyP4 }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleFileDrop}
            >
              <input ref={fileInputRef} type="file" accept=".ldt,.ies" onChange={handleFileChange} className="hidden" />
              {ldcFile ? (
                <div className="flex items-center justify-center gap-2 text-green01">
                  <Upload className="w-5 h-5" />
                  <span className="text-sm">{ldcFile.name}</span>
                </div>
              ) : (
                <div className="text-greyP3">
                  <Upload className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-sm">{t('catalogue.dragDropOrClick')}</p>
                  <p className="text-xs mt-1">{t('catalogue.supportedFormats')}: .ldt, .ies</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-full text-white text-sm hover:bg-greyP4 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!isValid}
              className="px-6 py-2 rounded-full bg-green01 text-black text-sm font-medium
                hover:bg-green03 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEditMode ? t('common.save') : t('catalogue.addVariant')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVariantModal;
